import React, { useCallback, useMemo, useState } from 'react';

import { useTasksState } from '../helpers';
import { TasksContext } from './tasks_context';

import type { ReactNode } from 'react';
import type { ForUser, Task } from 'bundles/apex/tasks/types';
import type { Parent } from 'bundles/crm/types';
import type { Props as TaskProps } from '../task';
import type { TasksContextValue, TasksPaths } from './tasks_context';

export interface TasksProviderProps {
  children: ReactNode;
  currentParent?: Parent;
  dateRangeEnabled?: boolean;
  features?: TaskProps['features'];
  forUser: ForUser;
  hideAssignees?: boolean;
  inline?: boolean;
  options?: TasksContextValue['options'];
  paginate?: boolean;
  paths: TasksPaths;
  showParents?: TaskProps['showParents'];
  showRouterPrompt?: boolean;
  tasks?: Task[];
  tbdEnabled?: boolean;
}

export function TasksProvider({
  children,
  currentParent,
  dateRangeEnabled,
  features,
  forUser,
  hideAssignees = false,
  inline = false,
  options = {},
  paginate = false,
  paths,
  showParents,
  showRouterPrompt = false,
  tasks: externalTasks,
  tbdEnabled,
}: TasksProviderProps) {
  const state = useTasksState({ dateRangeEnabled, paginate, paths, tasks: externalTasks, tbdEnabled });

  const [showModal, setShowModal] = useState(false);

  const showTaskModal = useCallback((task?: Task) => {
    state.setSelectedTask(task || null);
    setShowModal(true);
  }, [state.setSelectedTask]);

  const value = useMemo<TasksContextValue>(
    () => ({
      tasks: state.tasks,
      loading: state.loading,
      pagination: state.pagination,
      selectedTask: state.selectedTask,
      showModal,

      setFilters: state.setFilters,
      setLoading: state.setLoading,
      setPage: state.setPage,
      setSelectedTask: state.setSelectedTask,
      setShowModal,
      showTaskModal,
      reloadTasks: state.reloadTasks,
      createTask: state.createTask,
      updateTask: state.updateTask,
      deleteTask: state.deleteTask,
      addTaskAttachment: state.addTaskAttachment,
      removeTaskAttachment: state.removeTaskAttachment,

      currentParent,
      features,
      forUser,
      hideAssignees,
      inline,
      options,
      paginate,
      paths,
      showParents,
      showRouterPrompt,
    }),
    [
      state.tasks,
      state.loading,
      state.pagination,
      state.selectedTask,
      showModal,
      state.setFilters,
      state.setLoading,
      state.setPage,
      state.setSelectedTask,
      showTaskModal,
      state.reloadTasks,
      state.createTask,
      state.updateTask,
      state.deleteTask,
      state.addTaskAttachment,
      state.removeTaskAttachment,
      currentParent,
      features,
      forUser,
      hideAssignees,
      inline,
      options,
      paginate,
      paths,
      showParents,
      showRouterPrompt,
    ],
  );

  return (
    <TasksContext.Provider value={value}>
      {children}
    </TasksContext.Provider>
  );
}
